import { useEffect, useState, type FormEvent } from "react";
import { PageHeader, LoadingState, ErrorState } from "@/components/PageHeader";
import { Button, Input, Label, Textarea } from "@/components/ui/form";
import { Card, CardBody, DataTable, DataTableHead, DataTableTd, DataTableTh } from "@/components/ui/card";
import { apiFetch } from "@/utils/api";

interface Contact {
  id?: string;
  name: string;
  email: string;
  phone: string;
  isPrimary?: boolean;
}

interface Customer {
  id: string;
  company_name: string;
  address: string;
  notes: string;
  contacts: Contact[];
}

export function AdminCustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ companyName: "", address: "", notes: "", contacts: [{ name: "", email: "", phone: "", isPrimary: true }] as Contact[] });

  const load = () => apiFetch<{ customers: Customer[] }>("/customers").then((d) => setCustomers(d.customers));

  useEffect(() => {
    load().catch((e) => setError(e instanceof Error ? e.message : "Failed")).finally(() => setLoading(false));
  }, []);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    await apiFetch("/customers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        companyName: form.companyName,
        address: form.address,
        notes: form.notes,
        contacts: form.contacts.filter((c) => c.name.trim()),
      }),
    });
    setShowNew(false);
    setForm({ companyName: "", address: "", notes: "", contacts: [{ name: "", email: "", phone: "", isPrimary: true }] });
    await load();
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div>
      <PageHeader title="Customers" actions={<Button onClick={() => setShowNew(!showNew)}>{showNew ? "Cancel" : "Add Customer"}</Button>} />
      {showNew && (
        <Card className="mb-6 max-w-lg">
          <CardBody>
            <form onSubmit={create} className="space-y-4">
              <div><Label>Company Name</Label><Input value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} required /></div>
              <div><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><Label>Contact Name</Label><Input value={form.contacts[0].name} onChange={(e) => setForm({ ...form, contacts: [{ ...form.contacts[0], name: e.target.value }] })} /></div>
              <div><Label>Contact Email</Label><Input value={form.contacts[0].email} onChange={(e) => setForm({ ...form, contacts: [{ ...form.contacts[0], email: e.target.value }] })} /></div>
              <Button type="submit">Create</Button>
            </form>
          </CardBody>
        </Card>
      )}
      <Card>
        <CardBody className="pt-2">
          <DataTable>
            <DataTableHead>
              <DataTableTh>Company</DataTableTh>
              <DataTableTh>Contacts</DataTableTh>
              <DataTableTh>Address</DataTableTh>
            </DataTableHead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className="border-t border-gray-50 hover:bg-gray-50/80">
                  <DataTableTd className="font-semibold">{c.company_name}</DataTableTd>
                  <DataTableTd>{(c.contacts ?? []).map((x) => x.name).join(", ") || "—"}</DataTableTd>
                  <DataTableTd>{c.address || "—"}</DataTableTd>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </CardBody>
      </Card>
    </div>
  );
}
